import React from "react";

const baseUrl = process.env.REACT_APP_API_URL || "";

export interface CampaignImage {
  id?: number;
  url: string;
  publicUrl?: string;
  filename?: string;
  uploadedAt?: string;
}

interface CampaignHeroImagePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  title?: string;
}

const CampaignHeroImagePicker: React.FC<CampaignHeroImagePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  title = "Choose a hero image",
}) => {
  const [images, setImages] = React.useState<CampaignImage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const loadImages = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${baseUrl}/api/campaigns/images`);
        if (!res.ok) {
          const text = await res.text();
          console.error("Campaign images load error:", res.status, text);
          throw new Error(`Failed to load campaign images: ${res.status}`);
        }

        const raw = await res.json();
        let list: CampaignImage[] = [];

        if (Array.isArray(raw)) {
          list = raw as CampaignImage[];
        } else if (Array.isArray((raw as any).images)) {
          list = (raw as any).images as CampaignImage[];
        } else if (Array.isArray((raw as any).items)) {
          list = (raw as any).items as CampaignImage[];
        } else {
          console.error("Unexpected campaign images API shape:", raw);
          throw new Error("Unexpected campaign images API shape");
        }

        setImages(list);
      } catch (err: any) {
        console.error("Error loading campaign images:", err);
        setError(err?.message || "Failed to load images.");
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, [isOpen]);

  function resolveImageUrl(img: CampaignImage): string {
    const src = img.publicUrl || img.url;
    if (!src) return "";
    if (src.startsWith("http://") || src.startsWith("https://")) return src;
    return `${baseUrl}${src.startsWith("/") ? src : `/${src}`}`;
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex-1 overflow-y-auto">
          {loading && (
            <p className="text-sm text-gray-500">Loading images…</p>
          )}

          {error && (
            <p className="text-sm text-red-600 mb-2">
              Error loading images: {error}
            </p>
          )}

          {!loading && !error && images.length === 0 && (
            <p className="text-sm text-gray-500">
              No hero images found yet. Upload one from the form, then it will
              appear here for reuse.
            </p>
          )}

          {!loading && !error && images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img, idx) => {
                const fullUrl = resolveImageUrl(img);
                const label =
                  img.filename ||
                  img.publicUrl ||
                  img.url ||
                  `Image ${idx + 1}`;

                return (
                  <button
                    key={img.id ?? `${img.url}-${idx}`}
                    type="button"
                    className="group flex flex-col items-stretch text-left text-xs border border-gray-200 rounded-md overflow-hidden hover:border-blue-400 hover:shadow-sm bg-white"
                    onClick={() => {
                      if (!fullUrl) return;
                      onSelect(fullUrl);
                      onClose();
                    }}
                  >
                    {fullUrl ? (
                      <img
                        src={fullUrl}
                        alt={label}
                        className="h-24 w-full object-cover"
                      />
                    ) : (
                      <div className="h-24 w-full flex items-center justify-center text-gray-400 text-[11px]">
                        (no preview)
                      </div>
                    )}
                    <div className="px-2 py-1 border-t border-gray-100">
                      <div className="truncate text-[11px] text-gray-700">
                        {label}
                      </div>
                      {img.uploadedAt && (
                        <div className="text-[10px] text-gray-400">
                          {new Date(img.uploadedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 text-[11px] text-gray-500">
          Click an image to use it as the hero image for this campaign.
        </div>
      </div>
    </div>
  );
};

export default CampaignHeroImagePicker;
